/***************************************************************************/
"use client";
/***************************************************************************/

import {
  TPostUIErrorRequestData,
  TPostUIErrorResponseData,
} from "@/app/api/errors/ui/interfaces.universal";
import { RBAC } from "@/client/components/rbac";
import { Button, ButtonIcon } from "@/client/components/ui/button";
import { ButtonGroup } from "@/client/components/ui/button-group";
import { Flex } from "@/client/components/ui/flex";
import {
  Result,
  ResultIcon,
  ResultMessage,
  ResultTitle,
} from "@/client/components/ui/result";
import TMayBe from "@/universal/types/may-be";
import { ArrowRightIcon, RotateCwIcon, UnplugIcon } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutate } from "../api/use-mutate";

const RouteError: React.FC<{
  reloadUrl?: string;
  error?: TMayBe<{ message?: string; stack?: string }>;
  size?: "default" | "sm" | "lg";
}> = ({ reloadUrl, error, size }) => {
  const { t: tR } = useTranslation("r");
  const { t: tO } = useTranslation("o");
  const { t: tError } = useTranslation("error");
  const pathName = usePathname();
  const [, mutator] = useMutate<
    TPostUIErrorRequestData,
    TPostUIErrorResponseData
  >();

  useEffect(() => {
    mutator.post("/errors/ui", {
      error: JSON.stringify({
        message: error?.message ?? tError("pageError.title"),
        stack: error?.stack,
      }),
      url: window.location.href,
    });
  }, []);

  return (
    <Flex direction="horizontal" justify-content="center">
      <div className="p-[5vh] max-w-[600px]">
        <Result variant="warning" size={size}>
          <ResultIcon>
            <UnplugIcon />
          </ResultIcon>
          <ResultTitle>{tError("pageError.title")}</ResultTitle>
          <ResultTitle>
            <RBAC permissions={["AIRFI_ONLY"]}>{error?.message}</RBAC>
          </ResultTitle>
          <ResultMessage>{tError("pageError.description1")}</ResultMessage>
          <ResultMessage>{tError("pageError.description2")}</ResultMessage>
          <RBAC permissions={["AIRFI_ONLY"]}>
            <ResultMessage>{error?.stack}</ResultMessage>
          </RBAC>
          <ButtonGroup className="mt-4" align-items="center">
            <a href={reloadUrl ?? window.location.href}>
              <Button>
                <ButtonIcon>
                  <RotateCwIcon />
                </ButtonIcon>
                <span>{tR("reload")}</span>
              </Button>
            </a>
            {pathName !== "/" && (
              <React.Fragment>
                <span className="text-secondary text-sm">{tO("or")}</span>
                <Link href="/">
                  <Button>
                    <span>{tError("goToHome")}</span>
                    <ButtonIcon>
                      <ArrowRightIcon />
                    </ButtonIcon>
                  </Button>
                </Link>
              </React.Fragment>
            )}
          </ButtonGroup>
        </Result>
      </div>
    </Flex>
  );
};

export default RouteError;
